import React, { useEffect, useRef, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { api, API } from '../lib/api';
import { toast } from 'sonner';
import {
  Video, Loader2, Film, Clapperboard, Plus, Trash2, RefreshCw, Download, Play, Pause,
  Sparkles, Hand, GalleryVertical, Square as SquareIcon, Monitor, Smartphone, Camera, UserCheck, Coins
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import CreditsFab from '../components/jixxeo/CreditsFab';
import { inspectPrompt } from '../lib/promptGuard';
import PromptGuardWarning from '../components/jixxeo/PromptGuardWarning';
import { ShieldAlert } from 'lucide-react';

const DURATIONS = [4, 8, 12, 16, 20, 30, 45, 60, 90, 120, 300];
const ASPECTS = [
  { v: '9:16', label: 'Vertical', sub: 'TikTok / Reels', Icon: Smartphone },
  { v: '16:9', label: 'Landscape', sub: 'YouTube', Icon: Monitor },
  { v: '1:1',  label: 'Square',    sub: 'Instagram feed', Icon: SquareIcon },
  { v: '4:5',  label: 'Portrait',  sub: 'Insta portrait', Icon: GalleryVertical },
];
const CAMERAS = ['Static', 'Slow push-in', 'Pull-out', 'Pan left', 'Pan right', 'Tracking', 'Handheld', 'Drone', 'Cinematic'];
const QUALITIES = [{v:'standard', label:'Standard (720p)'}, {v:'hd', label:'HD (1080p)'}];
const STYLES = ['cinematic','photorealistic','anime','cyberpunk','3d','painting'];

function formatDur(sec) {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec/60), s = sec%60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

export default function VideoStudio() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState('simple');
  const [insufficient, setInsufficient] = useState(null); // { needed, balance, message }
  const [caps, setCaps] = useState(null);
  const [balance, setBalance] = useState(user?.credits_balance ?? 0);
  const [downloading, setDownloading] = useState(null);
  const [publishing, setPublishing] = useState(null);

  const refreshBalance = async () => {
    try {
      const { data } = await api.get('/credits/me');
      setBalance(data.balance);
    } catch {}
  };

  useEffect(() => {
    api.get('/ai/video/capabilities').then(r => {
      setCaps(r.data);
      // Simple-mode durations are the fixed trio 4 / 8 / 30. No auto-snap needed.
    }).catch(() => {});
    refreshBalance();
    // eslint-disable-next-line
  }, []);

  const [prompt, setPrompt] = useState('');
  const [guardAck, setGuardAck] = useState(false);
  const guard = inspectPrompt(prompt);
  useEffect(() => { setGuardAck(false); }, [prompt]);
  const [duration, setDuration] = useState(8);
  const [aspect, setAspect] = useState('9:16');
  const [style, setStyle] = useState('cinematic');
  const [quality, setQuality] = useState('standard');
  const [camera, setCamera] = useState('Cinematic');
  const [character, setCharacter] = useState(true);

  // Simple mode state
  const [simpleJob, setSimpleJob] = useState(null);
  const [simpleProgress, setSimpleProgress] = useState(0);
  const [simpleLoading, setSimpleLoading] = useState(false);

  // Story mode state
  const [project, setProject] = useState(null);
  const [storyBusy, setStoryBusy] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const simpleCost = (quality === 'hd' ? caps?.credit_costs?.simple_hd : caps?.credit_costs?.simple_standard) ?? 5;
  const sceneCost = caps?.credit_costs?.story_scene ?? 5;
  const storyScenes = Math.ceil((duration || 8) / 8);
  const storyTotalCost = storyScenes * sceneCost;

  const handle402 = (err) => {
    const d = err?.response?.data?.detail;
    if (d && typeof d === 'object' && d.error === 'insufficient_credits') {
      setInsufficient(d);
      return true;
    }
    return false;
  };

  // ============== SIMPLE MODE ==============
  const generateSimple = async () => {
    if (!prompt.trim()) return toast.error('Enter a prompt');
    if (!user) { toast.error('Please sign in first'); return; }
    const check = inspectPrompt(prompt);
    if (!check.ok && !guardAck) {
      toast.warning('Prompt may trip the safety filter — review the warning and tap Generate again to proceed.');
      return;
    }
    if (balance < simpleCost) { setInsufficient({ needed: simpleCost, balance, message: "You don't have enough credits to create this video." }); return; }
    setSimpleLoading(true); setSimpleProgress(5);
    try {
      const { data } = await api.post('/ai/video/generate', { prompt, duration, aspect_ratio: aspect, style, quality });
      setSimpleJob(data);
      if (data.balance != null) setBalance(data.balance);
      let p = 5;
      const iv = setInterval(async () => {
        p = Math.min(p + 10, 95); setSimpleProgress(p);
        try {
          const s = await api.get(`/ai/video/status/${data.job_id}`);
          if (s.data.status === 'ready') {
            clearInterval(iv); setSimpleProgress(100); setSimpleJob(s.data); setSimpleLoading(false);
            if (s.data.credits_refunded) toast.info('Jixxeo Cinema is in preview mode right now — your credits were refunded.');
            else toast.success('Video ready!');
            refreshBalance();
          }
        } catch {}
      }, 1500);
    } catch (e) {
      if (!handle402(e)) toast.error('Failed');
      setSimpleLoading(false);
    }
  };

  // ============== STORY MODE ==============
  const planStory = async () => {
    if (!prompt.trim()) return toast.error('Enter a story prompt');
    if (!user) { toast.error('Please sign in first'); return; }
    const check = inspectPrompt(prompt);
    if (!check.ok && !guardAck) {
      toast.warning('Prompt may trip the safety filter — review the warning and tap again to proceed.');
      return;
    }
    setStoryBusy(true);
    try {
      const { data } = await api.post('/ai/cinema/project', {
        prompt, duration, aspect_ratio: aspect, style, quality, camera,
        character_consistency: character, story_mode: true,
      });
      setProject(data);
      toast.success(`Scene plan ready — ${data.scenes.length} scenes · ${data.scenes.length * sceneCost} credits to render`);
    } catch (e) {
      if (!handle402(e)) toast.error(e.response?.data?.detail || 'Failed');
    } finally { setStoryBusy(false); }
  };

  const renderStory = async () => {
    if (!project) return;
    const needed = project.scenes.length * sceneCost;
    if (balance < needed) { setInsufficient({ needed, balance, message: `You need ${needed} credits to render this ${project.scenes.length}-scene story.` }); return; }
    setStoryBusy(true);
    try {
      const { data } = await api.post(`/ai/cinema/project/${project.id}/render`);
      if (data?.balance != null) setBalance(data.balance);
      // Poll every 1.5s until all scenes ready
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/ai/cinema/project/${project.id}`);
          setProject(data);
          if (data.status === 'ready') { clearInterval(pollRef.current); setStoryBusy(false); toast.success('All scenes rendered!'); refreshBalance(); }
        } catch {}
      }, 1500);
    } catch (e) { if (!handle402(e)) toast.error('Render failed'); setStoryBusy(false); }
  };

  const regenScene = async (idx) => {
    if (!project) return;
    try {
      const { data } = await api.post(`/ai/cinema/project/${project.id}/scene/${idx}/regenerate`);
      toast.info(`Regenerating scene ${idx + 1} — ${data.credits_charged} cr`);
      refreshBalance();
      if (!pollRef.current) {
        pollRef.current = setInterval(async () => {
          try { const { data } = await api.get(`/ai/cinema/project/${project.id}`); setProject(data);
            if (data.scenes.every(s => s.status === 'ready')) { clearInterval(pollRef.current); pollRef.current = null; refreshBalance(); }
          } catch {}
        }, 1500);
      }
    } catch (e) { if (!handle402(e)) toast.error('Failed'); }
  };

  const downloadWatermarked = async (jobId, label = 'video') => {
    setDownloading(jobId);
    try {
      const token = localStorage.getItem('jixxeo_token');
      const res = await fetch(`${API}/videos/download/${jobId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || 'download failed');
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `jixxeo-ai_${label.replace(/[^A-Za-z0-9]+/g, '_').slice(0, 40) || 'video'}.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
      toast.success('Download ready — watermark baked in.');
    } catch (e) {
      toast.error('Could not prepare watermarked video. Try again.');
    } finally { setDownloading(null); }
  };

  const deleteScene = async (idx) => {
    if (!project || !window.confirm('Delete this scene?')) return;
    try { const { data } = await api.delete(`/ai/cinema/project/${project.id}/scene/${idx}`);
      // Reload
      const { data: d2 } = await api.get(`/ai/cinema/project/${project.id}`);
      setProject(d2); toast.success('Scene deleted');
    } catch { toast.error('Failed'); }
  };

  const editScene = async (idx) => {
    const newPrompt = window.prompt('Edit scene prompt:', project.scenes[idx].prompt);
    if (newPrompt == null || !newPrompt.trim()) return;
    try { await api.put(`/ai/cinema/project/${project.id}/scene/${idx}`, { prompt: newPrompt });
      const { data: d2 } = await api.get(`/ai/cinema/project/${project.id}`); setProject(d2); toast.success('Scene updated');
    } catch { toast.error('Failed'); }
  };

  const extendStory = async (extraSec) => {
    if (!project) return;
    try {
      await api.post(`/ai/cinema/project/${project.id}/extend`, {
        prompt: project.prompt, duration: extraSec, aspect_ratio: project.aspect_ratio, style: project.style,
      });
      const { data } = await api.get(`/ai/cinema/project/${project.id}`); setProject(data);
      toast.success(`Added +${extraSec}s to the project`);
    } catch { toast.error('Failed'); }
  };

  const newProject = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setProject(null); setPrompt(''); setDuration(30);
  };

  const sceneProgress = project
    ? Math.round(100 * project.scenes.filter(s => s.status === 'ready').length / Math.max(1, project.scenes.length))
    : 0;

  return (
    <div className="max-w-5xl mx-auto space-y-5">
      {/* Insufficient Credits dialog */}
      <Dialog open={!!insufficient} onOpenChange={(o) => !o && setInsufficient(null)}>
        <DialogContent data-testid="insufficient-credits-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Coins className="w-5 h-5 text-amber-400" /> Not enough credits</DialogTitle>
            <DialogDescription>
              {insufficient?.message || "You don't have enough credits to create this video."}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl p-3 bg-white/5 border border-white/10 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-slate-400">Needed</span><b className="text-rose-300">{insufficient?.needed} credits</b></div>
            <div className="flex justify-between"><span className="text-slate-400">Your balance</span><b className="text-amber-300">{insufficient?.balance} credits</b></div>
          </div>
          <DialogFooter className="gap-2">
            <Button data-testid="insufficient-cancel" variant="outline" onClick={() => setInsufficient(null)}>Cancel</Button>
            <Button data-testid="insufficient-buy" onClick={() => { setInsufficient(null); nav('/credits'); }} className="gradient-brand text-white">
              <Coins className="w-3.5 h-3.5 mr-1" /> Buy Credits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black flex items-center gap-2"><Clapperboard className="w-7 h-7 text-fuchsia-400" /> Jixxeo <span className="gradient-text">Cinema</span></h1>
          <p className="text-sm text-slate-400">Pay-as-you-go AI video. No subscription needed.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="cinema-credits-pill"
            onClick={() => nav('/credits')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 text-slate-950 font-bold text-sm shadow-lg shadow-amber-500/20 hover:scale-105 transition-transform"
          >
            <Coins className="w-4 h-4" />
            <span data-testid="cinema-balance-value">{balance.toLocaleString()}</span>
            <span className="text-[10px] opacity-80">credits</span>
          </button>
          {/* Mode toggle */}
          <div data-testid="cinema-mode-toggle" className="inline-flex p-1 rounded-full bg-white/5 border border-white/10">
            <button data-testid="cinema-mode-simple" onClick={() => setMode('simple')}
              className={`px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1.5 ${mode==='simple' ? 'gradient-brand text-white' : 'text-slate-400'}`}>
              <Video className="w-3.5 h-3.5" /> Simple
            </button>
            <button data-testid="cinema-mode-story" onClick={() => setMode('story')}
              className={`px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1.5 ${mode==='story' ? 'gradient-brand text-white' : 'text-slate-400'}`}>
              <Film className="w-3.5 h-3.5" /> Story Mode
            </button>
          </div>
        </div>
      </div>

      {/* Status disclosure (user-facing — no vendor names) */}
      {caps && (caps.last_error && !caps.live_mode ? (
        <div data-testid="cinema-balance-banner" className="rounded-xl p-3 bg-rose-500/10 border border-rose-500/40 text-rose-200 text-xs flex items-start gap-2">
          <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-bold">Jixxeo Cinema is temporarily on preview mode</div>
            <div>Live rendering is paused. Your credits are not consumed and a cinematic preview is shown instead.</div>
            <div className="text-rose-300/80 mt-0.5">Try again shortly — service will resume automatically.</div>
          </div>
        </div>
      ) : caps.live_mode ? (
        <div data-testid="cinema-live-banner" className="rounded-xl p-3 bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-xs flex items-center gap-2">
          <Sparkles className="w-4 h-4 flex-shrink-0" />
          <span>✨ Jixxeo Cinema is live. Your video will render in about 30–90 seconds.</span>
        </div>
      ) : (
        <div data-testid="cinema-preview-banner" className="rounded-xl p-3 bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs flex items-center gap-2">
          <Sparkles className="w-4 h-4 flex-shrink-0" />
          <span>✨ Jixxeo Cinema is in preview mode. Credits are not consumed; a cinematic preview is shown.</span>
        </div>
      ))}

      {/* ========== Simple Mode ========== */}
      {mode === 'simple' && (
        <Card className="glass p-5 space-y-4" data-testid="cinema-simple-panel">
          <div>
            <Label className="text-xs">Prompt</Label>
            <Textarea data-testid="cinema-simple-prompt" value={prompt} onChange={e => setPrompt(e.target.value)} rows={3} placeholder="A giant bird flying over the mountains at sunrise" />
            <div className="mt-2"><PromptGuardWarning warnings={guard.warnings} ack={guardAck} onAckChange={setGuardAck} /></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div>
              <Label className="text-xs">Duration</Label>
              <div className="grid grid-cols-3 gap-1.5">
                {[4, 8, 30].map(d => {
                  const costKey = d >= 30 ? 'simple_30s' : (quality === 'hd' ? 'simple_hd' : 'simple_standard');
                  const cost = caps?.credit_costs?.[costKey];
                  return (
                    <button
                      key={d}
                      type="button"
                      data-testid={`cinema-simple-dur-${d}`}
                      onClick={() => setDuration(d)}
                      className={`py-2 px-2 rounded-md border text-xs font-bold transition-colors
                        ${duration === d
                          ? 'gradient-brand text-white border-transparent'
                          : 'bg-white/5 border-white/15 text-slate-300 hover:border-white/30'}`}
                    >
                      <div>{d}s</div>
                      {cost != null && <div className="text-[9px] opacity-80 font-normal">{cost} cr</div>}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                {duration === 30
                  ? '30-second Seedance clip with native audio'
                  : (caps?.audio_enabled ? 'With synchronized audio' : 'max 8s')}
              </div>
            </div>
            <div>
              <Label className="text-xs">Aspect</Label>
              <select value={aspect} onChange={e => setAspect(e.target.value)} className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm">
                {ASPECTS.map(a => <option key={a.v} value={a.v}>{a.v} {a.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Style</Label>
              <select value={style} onChange={e => setStyle(e.target.value)} className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm">
                {STYLES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Quality</Label>
              <select data-testid="cinema-simple-quality" value={quality} onChange={e => setQuality(e.target.value)} className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm">
                {QUALITIES.map(q => <option key={q.v} value={q.v}>{q.label}</option>)}
              </select>
              <div className="text-[10px] text-slate-500 mt-1">{quality === 'hd' ? `${caps?.credit_costs?.simple_hd ?? 8} cr` : `${caps?.credit_costs?.simple_standard ?? 5} cr`}</div>
            </div>
          </div>
          <Button data-testid="cinema-simple-generate" onClick={generateSimple} disabled={simpleLoading} className="gradient-brand text-white w-full">
            {simpleLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Coins className="w-4 h-4 mr-1.5" /> Generate — {simpleCost} credits</>}
          </Button>
          <div className="text-[11px] text-slate-500 text-center">Cost: {simpleCost} credits · Your balance: {balance}</div>
          {simpleLoading && <Progress value={simpleProgress} />}
          {simpleJob?.status === 'ready' && (
            <div className="pt-2">
              <video data-testid="cinema-simple-video" src={simpleJob.url} poster={simpleJob.poster_url} controls autoPlay loop playsInline muted className="w-full rounded-xl bg-black" />
              <div className="flex gap-2 mt-3 flex-wrap">
                <Button data-testid="cinema-simple-download" size="sm" variant="outline" disabled={downloading === simpleJob.id}
                  onClick={() => downloadWatermarked(simpleJob.id, simpleJob.prompt || 'simple')}>
                  {downloading === simpleJob.id ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Preparing…</> : <><Download className="w-3 h-3 mr-1" /> Download MP4</>}
                </Button>
                <Button
                  data-testid="cinema-simple-publish"
                  size="sm"
                  variant="outline"
                  disabled={publishing === simpleJob.id || simpleJob.public}
                  onClick={async () => {
                    setPublishing(simpleJob.id);
                    try {
                      await api.post(`/creations/${simpleJob.id}/publish`);
                      setSimpleJob(j => j ? { ...j, public: true } : j);
                      toast.success('Published to Discover → Videos');
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Publish failed');
                    } finally {
                      setPublishing(null);
                    }
                  }}
                  className="border-emerald-500/40 text-emerald-200"
                >
                  {publishing === simpleJob.id
                    ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Publishing…</>
                    : simpleJob.public
                      ? <><Film className="w-3 h-3 mr-1" /> Published ✓</>
                      : <><Film className="w-3 h-3 mr-1" /> Publish to Discover</>}
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setMode('story'); setPrompt(simpleJob.prompt); setDuration(30); }} className="border-violet-500/40 text-violet-200">
                  <Film className="w-3 h-3 mr-1" /> Extend to a full story in Story Mode
                </Button>
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Downloads are watermarked with "Jixxeo AI".</div>
            </div>
          )}
        </Card>
      )}

      {/* ========== Story Mode ========== */}
      {mode === 'story' && !project && (
        <Card className="glass p-5 space-y-4" data-testid="cinema-story-setup">
          <div>
            <Label className="text-xs">Story prompt</Label>
            <Textarea data-testid="cinema-story-prompt" value={prompt} onChange={e => setPrompt(e.target.value)} rows={4}
              placeholder="A rapper walks through downtown at night, gets into a black Lamborghini, drives through the city, arrives at a nightclub, and performs on stage." />
            <div className="mt-2"><PromptGuardWarning warnings={guard.warnings} ack={guardAck} onAckChange={setGuardAck} /></div>
          </div>

          <div>
            <Label className="text-xs mb-2 block">Target duration</Label>
            <div className="flex gap-2 flex-wrap">
              {DURATIONS.map(d => (
                <button key={d} data-testid={`cinema-dur-${d}`} onClick={() => setDuration(d)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors
                    ${duration===d ? 'gradient-brand text-white border-transparent' : 'bg-white/5 border-white/15 text-slate-300 hover:border-white/30'}`}>
                  {formatDur(d)}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">Jixxeo splits your story into {Math.ceil(duration/8)} connected 8-second scenes.</div>
          </div>

          <div>
            <Label className="text-xs mb-2 block">Aspect ratio</Label>
            <div className="grid grid-cols-4 gap-2">
              {ASPECTS.map(a => (
                <button key={a.v} data-testid={`cinema-aspect-${a.v}`} onClick={() => setAspect(a.v)}
                  className={`flex flex-col items-center gap-1 p-2 rounded-xl border text-center transition-colors
                    ${aspect===a.v ? 'bg-violet-500/20 border-violet-500/60' : 'bg-white/5 border-white/10 hover:border-white/25'}`}>
                  <a.Icon className="w-5 h-5 text-violet-300" />
                  <div className="text-xs font-bold">{a.v}</div>
                  <div className="text-[9px] text-slate-500">{a.sub}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div><Label className="text-xs">Style</Label>
              <select value={style} onChange={e => setStyle(e.target.value)} className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm">
                {STYLES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Quality</Label>
              <select value={quality} onChange={e => setQuality(e.target.value)} className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm">
                {QUALITIES.map(q => <option key={q.v} value={q.v}>{q.label}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Camera</Label>
              <select value={camera} onChange={e => setCamera(e.target.value)} className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm">
                {CAMERAS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Consistency</Label>
              <button data-testid="cinema-char" onClick={() => setCharacter(c => !c)}
                className={`w-full px-3 py-2 rounded-md text-sm border inline-flex items-center justify-center gap-1
                  ${character ? 'bg-violet-500/15 border-violet-400/40 text-violet-200' : 'bg-white/5 border-white/10 text-slate-400'}`}>
                <UserCheck className="w-3.5 h-3.5" /> Character {character ? 'ON' : 'OFF'}
              </button>
            </div>
          </div>

          <Button data-testid="cinema-plan-story" onClick={planStory} disabled={storyBusy} className="gradient-brand text-white w-full">
            {storyBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Planning…</> : <>Plan scenes →</>}
          </Button>
          <div className="text-[11px] text-slate-500 text-center">Estimated cost: ~{storyTotalCost} credits ({storyScenes} scenes × {sceneCost}) · Your balance: {balance}</div>
        </Card>
      )}

      {/* ========== Story Mode — project view ========== */}
      {mode === 'story' && project && (
        <Card className="glass p-5 space-y-4" data-testid="cinema-project">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-display font-black text-lg truncate">{project.title}</div>
              <div className="text-xs text-slate-400">{project.scenes.length} scenes · ~{formatDur(project.scenes.reduce((s,x) => s + (x.duration||0), 0))} · {project.aspect_ratio} · {project.quality}</div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="cinema-new-project" variant="outline" size="sm" onClick={newProject}>New</Button>
              {project.status !== 'rendering' && (
                <Button data-testid="cinema-render-all" onClick={renderStory} size="sm" className="gradient-brand text-white">
                  <Play className="w-3 h-3 mr-1" /> Render all — {project.scenes.length * sceneCost} cr
                </Button>
              )}
            </div>
          </div>

          {project.status === 'rendering' && (
            <div data-testid="cinema-render-status">
              <Progress value={sceneProgress} />
              <div className="text-xs text-slate-400 mt-1">Scene {project.scenes.filter(s=>s.status==='ready').length} of {project.scenes.length}</div>
            </div>
          )}

          {/* Scene timeline */}
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {project.scenes.map((s, i) => (
              <div key={i} data-testid={`cinema-scene-${i}`} className="flex-shrink-0 w-44 rounded-xl bg-white/5 border border-white/10 overflow-hidden">
                <div className="aspect-video bg-black relative">
                  {s.status === 'ready' && s.url
                    ? <video src={s.url} muted autoPlay loop playsInline className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex flex-col items-center justify-center text-xs text-slate-500 gap-1">
                        {s.status === 'processing' ? <Loader2 className="w-5 h-5 animate-spin text-violet-400" /> : <Film className="w-5 h-5 text-slate-600" />}
                        <span className="uppercase tracking-wider">{s.status}</span>
                      </div>}
                  <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-[10px] font-bold">#{i+1} · {s.duration}s</div>
                </div>
                <div className="p-2 space-y-1">
                  <div className="text-[11px] text-slate-300 line-clamp-2 min-h-[2.4em]">{s.prompt}</div>
                  <div className="flex gap-1">
                    <button data-testid={`cinema-scene-edit-${i}`} onClick={() => editScene(i)} className="flex-1 text-[10px] px-1.5 py-1 rounded bg-white/5 hover:bg-white/10">Edit</button>
                    <button data-testid={`cinema-scene-regen-${i}`} onClick={() => regenScene(i)} className="flex-1 text-[10px] px-1.5 py-1 rounded bg-white/5 hover:bg-white/10 inline-flex items-center justify-center gap-1"><RefreshCw className="w-2.5 h-2.5" /></button>
                    <button data-testid={`cinema-scene-delete-${i}`} onClick={() => deleteScene(i)} className="text-[10px] px-1.5 py-1 rounded bg-red-500/15 hover:bg-red-500/25 text-red-300"><Trash2 className="w-2.5 h-2.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Extend */}
          <div className="pt-2 border-t border-white/5">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Extend video</div>
            <div className="flex gap-2 flex-wrap">
              {[4,8,12,20,30,60].map(d => (
                <Button key={d} data-testid={`cinema-extend-${d}`} variant="outline" size="sm" onClick={() => extendStory(d)}>+{d}s</Button>
              ))}
            </div>
          </div>

          {/* Final preview */}
          {project.status === 'ready' && project.final_url && (
            <div className="pt-3 border-t border-white/5">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Final preview (scene 1)</div>
              <video data-testid="cinema-final-video" src={project.final_url} poster={project.scenes[0]?.poster_url} controls autoPlay loop playsInline muted className="w-full rounded-xl bg-black" />
              <div className="flex gap-2 mt-3 flex-wrap">
                <Button data-testid="cinema-final-download" variant="outline" size="sm" disabled={downloading === project.id}
                  onClick={() => downloadWatermarked(project.id, project.title || project.prompt || 'story')}>
                  {downloading === project.id ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Preparing…</> : <><Download className="w-3 h-3 mr-1" /> Download MP4</>}
                </Button>
                <Button
                  data-testid="cinema-final-publish"
                  variant="outline"
                  size="sm"
                  disabled={publishing === project.id || project.public}
                  onClick={async () => {
                    setPublishing(project.id);
                    try {
                      await api.post(`/creations/${project.id}/publish`);
                      setProject(p => p ? { ...p, public: true } : p);
                      toast.success('Published to Discover → Videos');
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Publish failed');
                    } finally {
                      setPublishing(null);
                    }
                  }}
                  className="border-emerald-500/40 text-emerald-200"
                >
                  {publishing === project.id
                    ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Publishing…</>
                    : project.public
                      ? <><Film className="w-3 h-3 mr-1" /> Published ✓</>
                      : <><Film className="w-3 h-3 mr-1" /> Publish to Discover</>}
                </Button>
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Downloads are watermarked with "Jixxeo AI".</div>
            </div>
          )}
        </Card>
      )}
      <CreditsFab balance={balance} />
    </div>
  );
}
